import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { uniq, difference, uniqBy } from 'lodash';

import {
  reduxForm,
  SubmissionError,
} from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import FormError from 'components/common/FormError';
import ProductClaim from 'components/workflow/ProductClaim';
import OrderInformation from 'components/workflow/OrderInformation';
import Link from 'components/common/Link';

import objectDiff from 'utils/objectDiff';
import formatPrice from 'utils/formatPrice';
import formatSinglePlural from 'utils/formatSinglePlural';
import throttle from 'utils/throttle';
import * as validate from 'utils/validate';

import dateFormat from 'config/dateFormat';
import timeFormat from 'config/timeFormat';
import timeDateFormat from 'config/timeDateFormat';
import orderLineTypes from 'config/orderLineTypes';
import transmissionMethods from 'config/transmissionMethods';

import {
  createOrder,
  bulkUpdateOrders,
  addOrderResource,
  removeOrderResource,
  changeOrderResource,
  removeOrderInsuranceParams,
} from 'redux/modules/orders';
import {
  createOrderLines,
  updateOrderLines,
  deleteOrderLines,
} from 'redux/modules/ordersLines';
import {
  fetchConnectedOrders,
} from 'redux/modules/connectedOrders';
import { deleteOrderLineDocument } from 'redux/modules/ordersLinesDocuments';
import { addNotification } from 'redux/modules/notifications';

const styles = theme => ({
  formError: {
    marginTop: 15,
    marginBottom: 15,
  },
  gridContainer: {
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  gridItemShrink: {
    flexShrink: 1,
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100%',
      flexBasis: '100%',
    },
  },
  gridItemAside: {
    width: 350,
    maxWidth: 350,
    flexBasis: 350,
    [theme.breakpoints.up('lg')]: {
      width: 420,
      maxWidth: 420,
      flexBasis: 420,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100%',
      flexBasis: '100%',
    },
  },
});

let prevSyncUpdates = {};

function onSubmit(values, dispatch, props) {
  if (!values.orderLine || values.orderLine.length === 0) {
    throw new SubmissionError({
      _error: 'At least one product should be added to order',
      orderLine: {
        _error: 'At least one product should be added to order',
      },
    });
  }

  const normalizeOrderLine = orderLine => orderLine.map((item) => {
    const normalizedItem = Object.assign({}, item, {
      ...item.Modifiers ? { Modifiers: item.Modifiers.join('') } : {},
      ...item.Modifiers && item.Modifiers.filter(modifiersItem => modifiersItem).length === 0
        ? { Modifiers: null } : {},
      'Place of SRV': item.placeOfSRV,
      'Assigned User ID': item.assignedUserID,
      'Date of Service From': moment(item.dateOfServiceFrom, dateFormat).format(dateFormat),
      'Date of Service To': moment(item.dateOfServiceTo, dateFormat).format(dateFormat),
      ...item.driverID ? { 'Driver ID': item.driverID } : {},
      ...item.driverID === null
        && item.ID
        && props.initialValues.orderLine.filter(initialOrderLine =>
          initialOrderLine.ID === item.ID)[0].driverID
        ? { 'Driver ID': null } : {},
      ...item.Narrative === '' ? { Narrative: null } : {},
      ...item.Diagnosis && item.Diagnosis.length > 0 ? {
        'Diagnosis ID': item.Diagnosis
          .map(diagnosisItem => diagnosisItem.value),
        'Primary Diagnosis ID': [item.Diagnosis[0].value],
      } : {},
      ...item.Diagnosis === [] ? {
        'Diagnosis ID': [],
        'Primary Diagnosis ID': [],
      } : {},
      ...item.Status && item.Status.length > 0 ? {
        'Workflow Status ID': item.Status
          .map(statusItem => statusItem.value),
      } : {},
      ...item.Status && item.Status.filter(statusItem => statusItem.nextWorkflowStatusID).length > 0 ? {
          'Next Workflow Status ID': item.Status
            .map(statusItem => statusItem.nextWorkflowStatusID || statusItem.value)
        } : {},
      ...!item['Is Delivered']
        && item.Status
        && item.Status.filter(statusItem => statusItem.isDelivered).length > 0 ? {
        'Is Delivered': true,
      } : {},
    });
    if (normalizedItem.dateOfDelivery && normalizedItem.timeOfDelivery) {
      const hour = moment(item.timeOfDelivery, timeFormat).hour();
      const minute = moment(item.timeOfDelivery, timeFormat).minute();
      normalizedItem['Date of Delivery'] = moment(item.dateOfDelivery, dateFormat)
        .hour(hour)
        .minute(minute)
        .format(timeDateFormat);
    } else {
      normalizedItem['Date of Delivery'] = null;
    }
    if (normalizedItem['Calories Per Unit']) {
      normalizedItem.Units = Math.ceil(normalizedItem.Units * normalizedItem['Calories Per Unit'] / 100);
    }
    delete normalizedItem.Status;
    delete normalizedItem.Selected;
    delete normalizedItem['Product Name'];
    delete normalizedItem['Medicaid Description'];
    delete normalizedItem['Medicaid Rental Price'];
    delete normalizedItem['Medicaid Sale Price'];
    delete normalizedItem['Medicare Description'];
    delete normalizedItem['Medicare Rental Price'];
    delete normalizedItem['Medicare Sale Price'];
    delete normalizedItem['HCPC S Code'];
    delete normalizedItem['OR HCPC S Code'];
    delete normalizedItem['Is Main HCPC S Code'];
    delete normalizedItem.placeOfSRV;
    delete normalizedItem.dateOfServiceFrom;
    delete normalizedItem.dateOfServiceTo;
    delete normalizedItem.timestamp;
    delete normalizedItem.driverName;
    delete normalizedItem.selectedDriverName;
    delete normalizedItem.driverID;
    delete normalizedItem.dateOfDelivery;
    delete normalizedItem.timeOfDelivery;
    delete normalizedItem.Diagnosis;
    delete normalizedItem.assignedFromUserFirstName;
    delete normalizedItem.assignedFromUserLastName;
    delete normalizedItem.assignedUserName;
    delete normalizedItem.assignedUserID;
    delete normalizedItem['Calories Per Unit'];
    delete normalizedItem['Is Rejected'];
    delete normalizedItem['Retail Price Claims'];
    delete normalizedItem['Retail Price Self Pay'];
    normalizedItem.Charge = normalizedItem.Charge.replace(/[$,]/g, '');
    if (normalizedItem.Modifiers && normalizedItem.Modifiers.length === 0) {
      delete normalizedItem.Modifiers;
    }
    if (normalizedItem['Next Workflow Status ID'] && normalizedItem['Next Workflow Status ID'].length === 0) {
      delete normalizedItem['Next Workflow Status ID'];
    }
    if (normalizedItem.Cost && normalizedItem.Cost.length > 0) {
      normalizedItem.Cost = normalizedItem.Cost.replace(/[$,]/g, '');
    } else {
      normalizedItem.Cost = null;
    }
    if (normalizedItem.Type === 'Purchase') {
      delete normalizedItem.Refill;
    }
    return normalizedItem;
  });

  const normalizedValues = Object.assign({}, values, {
    'Order Line': normalizeOrderLine(values.orderLine),
  });
  const normalizedOrderLine = normalizedValues['Order Line'];
  normalizedValues['Insurance ID'] = normalizedValues.Insurances
    .map(item => item.value);
  normalizedValues['Primary Insurance ID'] = values.Insurances[0].value;
  if (normalizedValues['Insurance ID'].length > 0) {
    let newInsuranceParamValueItemPresent = false;
    let newInsuranceParamValue = normalizedValues['Order Insurance Param'].map((item) => {
      if (item['Insurance ID'] !== normalizedValues['Insurance ID'][0]) {
        return item;
      }
      newInsuranceParamValueItemPresent = true;
      return {
        ...item,
        'Insurance ID': normalizedValues['Insurance ID'][0],
        Authorization: normalizedValues.Authorization || null,
        'Authorization From': normalizedValues['Authorization From']
          ? normalizedValues['Authorization From'] : null,
        'Authorization To': normalizedValues['Authorization To']
          ? normalizedValues['Authorization To'] : null,
        Resubmission: normalizedValues.Resubmission || null,
        'Original Ref Number': normalizedValues['Original Ref Number'] || null,
      };
    });
    if (!newInsuranceParamValueItemPresent
      && normalizedValues['Insurance ID'][0] !== null) {
      newInsuranceParamValue = [
        ...normalizedValues['Order Insurance Param'],
        {
          'Insurance ID': normalizedValues['Insurance ID'][0],
          Authorization: normalizedValues.Authorization || null,
          'Authorization From': !validate.date(normalizedValues['Authorization From'])
            ? normalizedValues['Authorization From'] : null,
          'Authorization To': !validate.date(normalizedValues['Authorization To'])
            ? normalizedValues['Authorization To'] : null,
          Resubmission: normalizedValues.Resubmission || null,
          'Original Ref Number': normalizedValues['Original Ref Number'] || null,
        },
      ];
    }
    normalizedValues['Order Insurance Param'] = newInsuranceParamValue.filter(item =>
      normalizedValues['Insurance ID'].indexOf(item['Insurance ID']) !== -1);
  }
  delete normalizedValues.orderLine;
  delete normalizedValues['Company Name'];
  delete normalizedValues['Selected Company Name'];
  delete normalizedValues['Prescriber Name'];
  delete normalizedValues['Selected Prescriber Name'];
  delete normalizedValues['Referral Source Name'];
  delete normalizedValues['Selected Referral Source Name'];
  delete normalizedValues.Authorization;
  delete normalizedValues['Authorization From'];
  delete normalizedValues['Authorization To'];
  delete normalizedValues.Resubmission;
  delete normalizedValues['Original Ref Number'];
  delete normalizedValues.Insurances;
  if (props.variant === 'edit') {
    delete normalizedValues['Patient ID'];
    delete normalizedValues['Order Line'];
    const initialOrderLine = normalizeOrderLine(props.initialValues.orderLine);
    const createdOrderLine = normalizedOrderLine
      .filter(item => initialOrderLine
        .findIndex(nestedItem => nestedItem.ID === item.ID) === -1);
    const updatedOrderLine = normalizedOrderLine
      .reduce((accumulator, currentValue) => {
        const initialItem = initialOrderLine.find(item => item.ID === currentValue.ID);
        const currentValueDiff = initialItem ? objectDiff(currentValue, initialItem) : null;
        const removedDiagnosis = initialItem && initialItem['Diagnosis ID'] && initialItem['Diagnosis ID'].length > 0
          && !currentValue['Diagnosis ID'];
        if (currentValueDiff && Object.keys(currentValueDiff).length > 0) {
          return [
            ...accumulator,
            {
              ...currentValueDiff,
              ...currentValueDiff['Diagnosis ID'] || removedDiagnosis
                ? { 'Diagnosis ID': currentValue['Diagnosis ID'] } : {},
              ...currentValueDiff['Workflow Status ID']
                ? { 'Workflow Status ID': currentValue['Workflow Status ID'] } : {},
              ID: currentValue.ID,
            },
          ];
        } else if (removedDiagnosis) {
          return [
            ...accumulator,
            {
              'Diagnosis ID': [],
              'Primary Diagnosis ID': [],
              ID: currentValue.ID,
            },
          ];
        }
        return accumulator;
      }, []);
    const initialInsuranceID = props.initialValues.Insurances
      .map(item => item.value)
      .filter(item => item);
    const removedOrderLine = initialOrderLine
      .filter(item => normalizedOrderLine
        .findIndex(nestedItem => nestedItem.ID === item.ID) === -1);
    const removedVitalsID = normalizedValues['Vitals ID'].length === 0
        && props.initialValues['Vitals ID'].length !== 0
      ? props.initialValues['Vitals ID']
      : [];
    const removedDiagnosisID = normalizedValues['Diagnosis ID'].length === 0
        && props.initialValues['Diagnosis ID'].length !== 0
      ? props.initialValues['Diagnosis ID']
      : [];
    const removedSalesRepresentativeID = normalizedValues['Sales Representative ID'].length === 0
        && props.initialValues['Sales Representative ID'].length !== 0
      ? props.initialValues['Sales Representative ID']
      : [];
    const removedInsuranceID = initialInsuranceID
      .filter(item => normalizedValues['Insurance ID'].indexOf(item) === -1);
    const addedInsuranceID = normalizedValues['Insurance ID']
      .filter(item => initialInsuranceID.indexOf(item) === -1);
    const { updatedInsuranceParam, createdInsuranceParam, deletedInsuranceParam } = normalizedValues['Order Insurance Param']
      .reduce((accumulator, currentValue) => {
        if (!currentValue.ID) {
          return {
            ...accumulator,
            createdInsuranceParam: [
              ...accumulator.createdInsuranceParam,
              currentValue,
            ],
          };
        }
        if (!currentValue.Authorization
          && !currentValue['Authorization From']
          && !currentValue['Authorization To']
          && !currentValue['Original Ref Number']
          && !currentValue.Resubmission) {
          return {
            ...accumulator,
            deletedInsuranceParam: [
              ...accumulator.deletedInsuranceParam,
              currentValue,
            ],
          };
        } else {
          const initialCurrentValue = props.initialValues['Order Insurance Param']
            .find(item => item.ID === currentValue.ID);
          if (Object.keys(objectDiff(currentValue, initialCurrentValue)).length > 0) {
            return {
              ...accumulator,
              updatedInsuranceParam: [
                ...accumulator.updatedInsuranceParam,
                currentValue,
              ],
            };
          }
          return accumulator;
        }
        return accumulator;
      }, {
        updatedInsuranceParam: [],
        createdInsuranceParam: [],
        deletedInsuranceParam: [],
      });
    const removedCMNOrderLine = updatedOrderLine.filter(item => item['CMN/AMB'] === null);
    const updatedValues = {
      ...normalizedValues['Facility ID'] !== props.initialValues['Facility ID']
        ? { 'Facility ID': normalizedValues['Facility ID'] } : {},
      ...normalizedValues['Transmission Method'] !== props.initialValues['Transmission Method']
        ? { 'Transmission Method': normalizedValues['Transmission Method'] } : {},
      ...normalizedValues['Prescriber ID'] !== props.initialValues['Prescriber ID']
        ? { 'Prescriber ID': normalizedValues['Prescriber ID'] } : {},
      ...normalizedValues['Referral Source ID'] !== props.initialValues['Referral Source ID']
        ? { 'Referral Source ID': normalizedValues['Referral Source ID'] } : {},
      ...normalizedValues['Referral Source ID'] !== props.initialValues['Referral Source ID']
        && normalizedValues['Referral Source ID'] === ''
        ? { 'Referral Source ID': null } : {},
      ...removedVitalsID.length === 0
        && normalizedValues['Vitals ID'].join('-') !== props.initialValues['Vitals ID'].join('-')
        ? { 'Vitals ID': normalizedValues['Vitals ID'] } : {},
      ...removedDiagnosisID.length === 0
        && normalizedValues['Diagnosis ID'].join('-') !== props.initialValues['Diagnosis ID'].join('-')
        ? { 'Diagnosis ID': normalizedValues['Diagnosis ID'] } : {},
      ...removedSalesRepresentativeID.length === 0
        && normalizedValues['Sales Representative ID'].join('-') !== props.initialValues['Sales Representative ID'].join('-')
        ? { 'Sales Representative ID': normalizedValues['Sales Representative ID'] } : {},
      ...values.Insurances[0].value !== props.initialValues.Insurances[0].value
        ? { 'Primary Insurance ID': values.Insurances[0].value } : {},
    };
    const mainHCPCCodeRemovedOrderLine = removedOrderLine ? removedOrderLine
      .filter(item => item.ID && props.initialValues.orderLine.filter(valuesItem =>
        (valuesItem.ID === item.ID) && valuesItem['Is Main HCPC S Code']).length > 0) : null;
    const HCPCCodeRemovedOrderLine = removedOrderLine ? removedOrderLine
      .filter(item => !item.ID || props.initialValues.orderLine.filter(valuesItem =>
        (valuesItem.ID === item.ID) && !valuesItem['Is Main HCPC S Code']).length > 0) : null;
    const removedInsuranceParamsID = removedInsuranceID.length > 0 || deletedInsuranceParam.length > 0
      ? [
        ...props.order['Order Insurance Param'].filter(item =>
          removedInsuranceID.indexOf(item['Insurance ID']) !== -1).map(item => item.ID),
        ...deletedInsuranceParam.map(item => item.ID),
      ] : null;
    const removedCMNId = removedCMNOrderLine.length > 0 ? props.order['Order Line'].find(item =>
      item.ID === removedCMNOrderLine[0].ID).CMN.ID : null;
    const bulkOrderPayload = {
      ...Object.keys(updatedValues).length > 0 ? { orderUpdate: {
        ID: normalizedValues.ID,
        ...updatedValues,
      } } : {},
      ...addedInsuranceID.length > 0 ? { addOrderResource: {
        ID: normalizedValues.ID,
        'Insurance ID': addedInsuranceID,
      }} : {},
      ...removedDiagnosisID.length > 0 ? { changeOrderResource: {
        ID: normalizedValues.ID,
        'Diagnosis ID': [],
      }} : {},
      ...(removedVitalsID.length > 0
      || removedSalesRepresentativeID.length > 0
      || removedInsuranceID.length > 0) ? { removeOrderResource: {
        ID: normalizedValues.ID,
        ...removedVitalsID.length > 0 ? { 'Vitals ID': removedVitalsID } : {},
        ...removedSalesRepresentativeID.length > 0 ? { 'Sales Representative ID': removedSalesRepresentativeID } : {},
        ...removedInsuranceID.length > 0 ? { 'Insurance ID': removedInsuranceID } : {},
      } } : {},
      
      ...createdInsuranceParam.length > 0 ? { createOrderInsuranceParams: createdInsuranceParam.map(insuranceParamItem => ({
        ...insuranceParamItem,
        'Order ID': normalizedValues.ID,
      })) } : {},
      ...updatedInsuranceParam.length > 0 ? { updateOrderInsuranceParams: updatedInsuranceParam } : {},
      ...removedInsuranceParamsID && removedInsuranceParamsID.length > 0
        ? { removeOrderInsuranceParams: removedInsuranceParamsID } : {},
      ...createdOrderLine.length > 0 ? { createOrderLines: createdOrderLine } : {},
      ...updatedOrderLine.length > 0 ? { updateOrderLines: updatedOrderLine } : {},
      ...removedOrderLine.length > 0 ? { deleteOrderLines: {
        ID: removedOrderLine.map(item => item.ID),
      } }  : {},
      ...removedCMNId ? { deleteOrderLineDocument: {
        ID: [removedCMNId],
      }} : {},
    };
    if (Object.keys(bulkOrderPayload).length === 0) {
      return new Promise((resolve, reject) => {
        resolve();
      });
    }
    return new Promise((resolve, reject) => {
      dispatch(bulkUpdateOrders({
        values: {
          [normalizedValues.ID]: bulkOrderPayload,
        },
        resolve,
        reject,
        showNotification: false,
      }));
    })
      .then((res) => new Promise((resolve, reject) => {
        const updatedOrderLineWithConnectedOrders = updatedOrderLine
          .filter(item => initialOrderLine
            .find(initialItem => initialItem.ID === item.ID
            && (initialItem.Type === 'Recurring' || initialItem.Type === 'Rental')))
          .filter((item) => {
            const initialItem = normalizeOrderLine(props.initialValues.orderLine)
              .find(orderLine => orderLine.ID === item.ID);
            const diff = objectDiff(item, initialItem);
            delete diff['Workflow Status ID'];
            return Object.keys(diff).length > 0;
          });
        if (updatedOrderLineWithConnectedOrders.length > 0) {
          dispatch(fetchConnectedOrders({
            triggerLoading: false,
            orderLineID: updatedOrderLineWithConnectedOrders.map(item => item.ID),
            orderID: normalizedValues.ID,
          }));
        }
        resolve(res);
      }))
      .then(res => res,
        (err) => {
          throw new SubmissionError({
            ...err,
            ...err.error ? { _error: err.error } : {},
            ...err.description ? { _error: err.description } : {},
            ...err.data && err.data.error ? { _error: err.data.error } : {},
          });
        },
      );
  }
  if (normalizedValues['Vitals ID'] && normalizedValues['Vitals ID'].length === 0) {
    delete normalizedValues['Vitals ID'];
  }
  if (normalizedValues['Referral Source ID'] === '' && props.variant === 'create') {
    delete normalizedValues['Referral Source ID'];
  }
  return new Promise((resolve, reject) => {
    dispatch(createOrder({
      values: normalizedValues,
      recoverOrderValues: {
        [values.orderLine[0]['Assigned User ID']]: values,
      },
      resolve,
      reject,
      showNotification: false,
    }));
  }).then(res => res,
    (err) => {
      throw new SubmissionError({
        ...err,
        ...err.description ? { _error: err.description } : {},
        ...err.params ? { _error: { text: err.error, params: err.params } } : {},
      });
    });
}

function onChange(values, dispatch, props, prevValues) {
  let facilityChanged = false;
  let priceState;
  let priceIsRural;
  if (values['Facility ID'] && values['Facility ID'] !== prevValues['Facility ID']) {
    facilityChanged = true;
    const selectedFacility = props.business.Facilities.find(facilitiesItem => facilitiesItem.ID === values['Facility ID']);
    priceState = selectedFacility && selectedFacility['Company Address'] ? selectedFacility['Company Address'].State : null;
    priceIsRural = selectedFacility && selectedFacility['Company Address'] ? selectedFacility['Company Address']['Is Rural'] : null;
  }
  let changedInsurance = null;
  if (values.Insurances
    && values.Insurances.length > 0) {
    if (values.Insurances.length < prevValues.Insurances.length) {
      const removedInsurance = prevValues.Insurances.find(prevInsurancesItem =>
        !values.Insurances.find(currentInsurancesItem => currentInsurancesItem.value === prevInsurancesItem.value));
      const removedInsuranceOrderLineParamsCodes = props.order['Order Line'].reduce((accumulator, currentValue) => {
        if (currentValue['Order Line Insurance Param']
            && currentValue['Order Line Insurance Param'].find(paramsItem => paramsItem['Insurance ID'] === removedInsurance.value)) {
          return [
            ...accumulator,
            currentValue['HCPC S Code']['HCPC S Code'],
          ];
        }
        return accumulator;
      }, []);
      if (removedInsuranceOrderLineParamsCodes.length > 0) {
        dispatch(addNotification({
          message: `Removed insurance ${removedInsurance.label} has linked Reimbursement in ${removedInsuranceOrderLineParamsCodes.join(', ')} `,
          variant: 'info',
        }));
      }
    }

    if (!prevValues.Insurances || !prevValues.Insurances[0]
      || values.Insurances[0].value !== prevValues.Insurances[0].value) {
      changedInsurance = values.Insurances[0];
      if (prevValues.Insurances
        && prevValues.Insurances[0] && prevValues.Insurances[0].value !== null) {
        let newInsuranceParamValueItemPresent = false;
        let newInsuranceParamValue = values['Order Insurance Param'].map((item) => {
          if (item['Insurance ID'] !== prevValues.Insurances[0].value) {
            return item;
          }
          newInsuranceParamValueItemPresent = true;
          return {
            ...item,
            'Insurance ID': prevValues.Insurances[0].value,
            Authorization: values.Authorization || null,
            'Authorization From': !validate.date(values['Authorization From'])
              ? values['Authorization From'] : null,
            'Authorization To': !validate.date(values['Authorization To'])
              ? values['Authorization To'] : null,
            Resubmission: values.Resubmission || null,
            'Original Ref Number': values['Original Ref Number'] || null,
          };
        });
        if (!newInsuranceParamValueItemPresent) {
          newInsuranceParamValue = [
            ...values['Order Insurance Param'],
            {
              'Insurance ID': prevValues.Insurances[0].value,
              Authorization: values.Authorization || null,
              'Authorization From': !validate.date(values['Authorization From'])
                ? values['Authorization From'] : null,
              'Authorization To': !validate.date(values['Authorization To'])
                ? values['Authorization To'] : null,
              Resubmission: values.Resubmission || null,
              'Original Ref Number': values['Original Ref Number'] || null,
            },
          ];
        }
        dispatch(props.change('Order Insurance Param', newInsuranceParamValue));
      }
      let currentInsuranceParam = values['Order Insurance Param']
        ? values['Order Insurance Param'].find(item =>
          item['Insurance ID'] === values.Insurances[0].value) : null;
      if (!currentInsuranceParam) {
        currentInsuranceParam = {
          Authorization: null,
          'Authorization From': null,
          'Authorization To': null,
          Resubmission: null,
          'Original Ref Number': null,
        };
      }
      dispatch(props.change('Authorization', currentInsuranceParam.Authorization));
      dispatch(props.change('Authorization From', currentInsuranceParam['Authorization From']));
      dispatch(props.change('Authorization To', currentInsuranceParam['Authorization To']));
      dispatch(props.change('Resubmission', currentInsuranceParam.Resubmission));
      dispatch(props.change('Original Ref Number', currentInsuranceParam['Original Ref Number']));
    }
  }

  let mainInsurance;
  if (changedInsurance) {
    mainInsurance = changedInsurance;
  } else if (values.Insurances && values.Insurances.length > 0) {
    mainInsurance = values.Insurances[0];
  }

  if (!props.submitting
    && values.orderLine
    && values.orderLine.length > 0
    && prevValues.orderLine) {
    let syncUpdates = {};
    if (props.syncUpdates) {
      const selectedOrderLineIndexes =
        values.orderLine.reduce((accumulator, currentValue, currentIndex) => (
          currentValue.Selected ? [
            ...accumulator,
            currentIndex,
          ] : accumulator), []);
      const selectedOrderLines = values.orderLine
        .filter((item, index) => selectedOrderLineIndexes.indexOf(index) !== -1);
      const selectedPrevOrderLines = prevValues.orderLine
        .filter((item, index) => selectedOrderLineIndexes.indexOf(index) !== -1);
      const nextSyncUpdates = objectDiff(selectedOrderLines, selectedPrevOrderLines)
        .filter(item => item)[0];
      const changeIndex = selectedOrderLineIndexes.find(index =>
        Object.keys(objectDiff(values.orderLine[index], prevValues.orderLine[index])).length > 0);
      syncUpdates = objectDiff(nextSyncUpdates, prevSyncUpdates);
      if (syncUpdates.Status) {
        syncUpdates.Status = values.orderLine[changeIndex].Status;
      }
      if (syncUpdates.Diagnosis) {
        syncUpdates.Diagnosis = values.orderLine[changeIndex].Diagnosis;
      }
      if (Object.keys(syncUpdates).length > 0) {
        prevSyncUpdates = syncUpdates;
      }
    }
    let addedOrderDiagnosisID;
    let removedOrderDiagnosisID;
    let addedOrderDiagnosisDXCode;
    let addedOrderDiagnosisIndex;
    let changedOrderDiagnosisID = false;
    if (values['Diagnosis ID']) {
      addedOrderDiagnosisID = prevValues['Diagnosis ID']
        ? values['Diagnosis ID']
          .filter(item => prevValues['Diagnosis ID'].indexOf(item) === -1)[0]
        : values['Diagnosis ID'][0];
      removedOrderDiagnosisID = prevValues['Diagnosis ID']
        ? prevValues['Diagnosis ID']
          .filter(item => values['Diagnosis ID'].indexOf(item) === -1)[0]
        : null;
      changedOrderDiagnosisID = prevValues['Diagnosis ID']
        && prevValues['Diagnosis ID'].join('-') !== values['Diagnosis ID'].join('-');
      addedOrderDiagnosisDXCode = addedOrderDiagnosisID
        ? uniqBy([
          ...props.Patient.Diagnosis || [],
          ...props.order && props.order.Diagnosis ? props.order.Diagnosis : [],
        ], 'ID').filter(diagnosisItem =>
          diagnosisItem.ID === addedOrderDiagnosisID)[0]['DX Code']
        : null;
      addedOrderDiagnosisIndex = values['Diagnosis ID'].length - 1;
    }
    const newOrderLine = values.orderLine.map((item, index) => {
      let newDiagnosis;
      if (changedOrderDiagnosisID) {
        newDiagnosis = item.Diagnosis.map((diagnosisItem) => {
          const diagnosisItemIndex = values['Diagnosis ID'].indexOf(diagnosisItem.value);
          const diagnosisPointer = 'ABCDEFGHIJKL'.slice(diagnosisItemIndex, diagnosisItemIndex + 1);
          return {
            ...diagnosisItem,
            label: `${diagnosisPointer}${diagnosisItem.label.slice(1)}`,
          };
        });
      }
      if (removedOrderDiagnosisID) {
        const removedOrderLineDiagnosisIndex = newDiagnosis.findIndex(diagnosisItem =>
          diagnosisItem.value === removedOrderDiagnosisID);
        newDiagnosis = removedOrderLineDiagnosisIndex !== -1 ? [
          ...newDiagnosis.slice(0, removedOrderLineDiagnosisIndex),
          ...newDiagnosis.slice(removedOrderLineDiagnosisIndex + 1),
        ] : newDiagnosis;
      }
      let newItem = Object.assign({}, {
        ...item,
        ...newDiagnosis
          ? { Diagnosis: newDiagnosis } : {},
        ...addedOrderDiagnosisID
          && (!item.Diagnosis
          || (item.Diagnosis.length < 4
            && item.Diagnosis.map(diagnosisItem => diagnosisItem.value)
              .indexOf(addedOrderDiagnosisID) === -1))
          ? { Diagnosis: [
            ...item.Diagnosis || [],
            {
              value: addedOrderDiagnosisID,
              label: `${'ABCDEFGHIJKL'.slice(addedOrderDiagnosisIndex, addedOrderDiagnosisIndex + 1)}: ${addedOrderDiagnosisDXCode}`,
            },
          ] } : {},
        Status: item.Status,
      });
      if (props.syncUpdates) {
        if (Object.keys(syncUpdates).length > 0 && newItem.Selected) {
          newItem = {
            ...newItem,
            ...syncUpdates,
            ...syncUpdates.Modifiers ? {
              Modifiers: newItem.Modifiers.map((modifiersItem, modifiersIndex) =>
                syncUpdates.Modifiers[modifiersIndex] || modifiersItem,
              ) } : {},
          };
        }
      }
      if (prevValues.orderLine[index]
        && prevValues.orderLine[index].driverName
        && !newItem.driverName) {
        newItem.driverID = null;
      }

      if (facilityChanged) {
        const HCPCCode = props.addedHCPCCodes.find(codesItem => codesItem.ID === newItem['HCPC S Code ID']);
        const medicaidPrice = HCPCCode['HCPC S Code Price'].find(priceItem => priceItem['Price Type'] === 'Medicaid'
          && priceItem.State === priceState);
        const medicarePrice = HCPCCode['HCPC S Code Price'].find(priceItem => priceItem['Price Type'] === 'Medicare'
          && priceItem.State === priceState);
        newItem = {
          ...newItem,
          ...mainInsurance && mainInsurance.profitPercentMedicaid && medicaidPrice && medicaidPrice.Description ? {
            Description: medicaidPrice.Description,
          } : {},
          ...mainInsurance && mainInsurance.profitPercentMedicare && medicarePrice && medicarePrice.Description ? {
            Description: medicarePrice.Description,
          } : {},
          ...!mainInsurance || (!mainInsurance.profitPercentMedicaid
            && !mainInsurance.profitPercentMedicare) ? {
              Description: (medicarePrice &&  medicarePrice.Description ? medicarePrice.Description : null)
                || (medicaidPrice && medicaidPrice.Description ? medicaidPrice.Description : null),
            } : {},
          ...medicaidPrice ? {
            'Medicaid Description': medicaidPrice.Description,
            'Medicaid Rental Price': priceIsRural ? medicaidPrice['Rental Price Rural'] : medicaidPrice['Rental Price'],
            'Medicaid Sale Price': priceIsRural ? medicaidPrice['Sale Price Rural'] : medicaidPrice['Sale Price'],
          } : {
            'Medicaid Description': '',
            'Medicaid Rental Price': '',
            'Medicaid Sale Price': '',
          },
          ...medicarePrice ? {
            'Medicare Description': medicarePrice.Description,
            'Medicare Rental Price': priceIsRural ? medicarePrice['Rental Price Rural'] : medicarePrice['Rental Price'],
            'Medicare Sale Price': priceIsRural ? medicarePrice['Sale Price Rural'] : medicarePrice['Sale Price'],
          } : {
            'Medicare Description': '',
            'Medicare Rental Price': '',
            'Medicare Sale Price': '',
          },
        };
      }

      if (prevValues.orderLine
        && prevValues.orderLine[index]) {
        if ((prevValues.orderLine[index].Type !== newItem.Type)
            || changedInsurance || facilityChanged) {
          if (newItem['Retail Price Claims'] && mainInsurance && mainInsurance.type === 'Normal') {
            newItem.Charge = formatPrice(newItem['Retail Price Claims']);
          } else if (newItem['Retail Price Self Pay'] && mainInsurance && mainInsurance.type === 'Self') {
            newItem.Charge = formatPrice(newItem['Retail Price Self Pay']);
          } else {
            if (newItem.Type && (newItem.Type === 'Purchase' || newItem.Type === 'Recurring')) {      
              if (mainInsurance && mainInsurance.profitPercentMedicaid !== null) {
                newItem.Charge = newItem['Medicaid Sale Price']
                  ? formatPrice(newItem['Medicaid Sale Price'] * ((100 + mainInsurance.profitPercentMedicaid) / 100)) : '';
              }
              if (mainInsurance && mainInsurance.profitPercentMedicare !== null) {
                newItem.Charge = newItem['Medicare Sale Price']
                  ? formatPrice(newItem['Medicare Sale Price'] * ((100 + mainInsurance.profitPercentMedicare) / 100)) : '';
              }
              if (!mainInsurance
                || (mainInsurance.profitPercentMedicaid === null
                  && mainInsurance.profitPercentMedicare === null)
                || (mainInsurance.profitPercentMedicaid === undefined
                  && mainInsurance.profitPercentMedicare === undefined)) {
                const maxSalePrice = Math.max(newItem['Medicaid Sale Price'], newItem['Medicare Sale Price']);
                if (maxSalePrice === newItem['Medicare Sale Price']) {
                  newItem.Charge = newItem['Medicare Sale Price']
                    ? formatPrice(newItem['Medicare Sale Price'] * ((100 + props.systemSettings['System Config'].PROFIT_PERCENT) / 100)) : '';
                } else {
                  newItem.Charge = newItem['Medicaid Sale Price']
                    ? formatPrice(newItem['Medicaid Sale Price'] * ((100 + props.systemSettings['System Config'].PROFIT_PERCENT) / 100)) : '';
                }
              }
            }
            if (newItem.Type && newItem.Type === 'Rental') {
              if (mainInsurance && mainInsurance.profitPercentMedicaid !== null) {
                newItem.Charge = newItem['Medicaid Rental Price']
                  ? formatPrice(newItem['Medicaid Rental Price'] * ((100 + mainInsurance.profitPercentMedicaid) / 100)) : '';
              }
              if (mainInsurance && mainInsurance.profitPercentMedicare !== null) {
                newItem.Charge = newItem['Medicare Rental Price']
                  ? formatPrice(newItem['Medicare Rental Price'] * ((100 + mainInsurance.profitPercentMedicare) / 100)) : '';
              }
              if (!mainInsurance
                || (mainInsurance.profitPercentMedicaid === null
                  && mainInsurance.profitPercentMedicare === null)
                || (mainInsurance.profitPercentMedicaid === undefined
                  && mainInsurance.profitPercentMedicare === undefined)) {
                const maxRentalPrice = Math.max(newItem['Medicaid Rental Price'], newItem['Medicare Rental Price']);
                if (maxRentalPrice === newItem['Medicare Rental Price']) {
                  newItem.Charge = newItem['Medicare Rental Price']
                    ? formatPrice(newItem['Medicare Rental Price'] * ((100 + props.systemSettings['System Config'].PROFIT_PERCENT) / 100)) : '';
                } else {
                  newItem.Charge = newItem['Medicaid Rental Price']
                    ? formatPrice(newItem['Medicaid Rental Price'] * ((100 + props.systemSettings['System Config'].PROFIT_PERCENT) / 100)) : '';
                }
              }
            }
          }
        }
      }
      return newItem;
    });
    dispatch(props.change('orderLine', newOrderLine));
  }

  // Prevent Duplicate IDs
  if (values['Vitals ID'] !== prevValues['Vitals ID']) {
    const uniqVitalsID = uniq(values['Vitals ID']);
    if (difference(values['Vitals ID'], uniqVitalsID).length > 0) {
      dispatch(props.change('Vitals ID', uniqVitalsID));
    }
  }

  if (values['Diagnosis ID'] !== prevValues['Diagnosis ID']) {
    const uniqDiagnosisID = uniq(values['Diagnosis ID']);
    if (difference(values['Diagnosis ID'], uniqDiagnosisID).length > 0) {
      dispatch(props.change('Diagnosis ID', uniqDiagnosisID));
    }
  }

  if (values['Sales Representative ID'] !== prevValues['Sales Representative ID']) {
    const uniqSalesRepresentativeID = uniq(values['Sales Representative ID']);
    if (difference(values['Sales Representative ID'], uniqSalesRepresentativeID).length > 0) {
      dispatch(props.change('Sales Representative ID', uniqSalesRepresentativeID));
    }
  }
  // End Prevent Duplicate IDs

  if (values['Facility ID']
    && values['Selected Company Name'] !== values['Company Name']) {
    dispatch(props.change('Facility ID', ''));
  }

  if (values['Prescriber ID']
    && values['Selected Prescriber Name'] !== values['Prescriber Name']) {
    dispatch(props.change('Prescriber ID', null));
  }

  if (values['Referral Source ID']
    && values['Selected Referral Source Name'] !== values['Referral Source Name']) {
    dispatch(props.change('Referral Source ID', ''));
  }
}

function onSubmitFail(errors) {
  if (errors) {
    let errorNames = Object.keys(errors).filter(item => item !== 'orderLine');
    const orderLineErrorNames = errors.orderLine && Array.isArray(errors.orderLine)
      ? errors.orderLine.reduce((accumulator, currentValue, index) => {
        if (!currentValue) {
          return accumulator;
        }
        const currentOrderLineErrorNames = Object.keys(currentValue).map(item =>
          `orderLine[${index}].${item}`);
        if (currentOrderLineErrorNames.length > 0) {
          return [
            ...accumulator,
            ...currentOrderLineErrorNames,
          ];
        }
        return accumulator;
      }, []) : [];
    errorNames = [
      ...orderLineErrorNames,
      ...errorNames,
    ];
    if (errorNames.length > 0) {
      const selectorString = errorNames.map(item => `[name="${item}"]`).join(', ');
      const firstErrorEl = document.querySelector(selectorString);
      if (firstErrorEl) {
        const firstErrorElOffsetTop = firstErrorEl.getBoundingClientRect().top;
        const windowScrollTop = window.scrollY !== undefined ? window.scrollY : window.scrollTop;
        const scrollOffset = 50;
        if ((window.innerHeight - scrollOffset) < firstErrorElOffsetTop) {
          const pageHeaderHeight = 64;
          window.scrollTo(0, (firstErrorElOffsetTop
            + windowScrollTop) - pageHeaderHeight - scrollOffset);
          firstErrorEl.focus();
        }
      }
    }
  }
}

const validateForm = (values) => {
  const errors = {};

  if (values['Transmission Method']
    && transmissionMethods.indexOf(values['Transmission Method']) === -1) {
    errors['Transmission Method'] = 'Invalid Transmission Method';
  }

  if (values['Company Name']
    && (!values['Facility ID'] || values['Facility ID'].length === 0)) {
    errors['Company Name'] = 'Please, select Facility from list';
  }

  if (values['Prescriber Name']
    && (!values['Prescriber ID'] || values['Prescriber ID'].length === 0)) {
    errors['Prescriber Name'] = 'Please, select Prescriber from list';
  }

  if (values['Referral Source Name']
    && (!values['Referral Source ID'] || values['Referral Source ID'].length === 0)) {
    errors['Referral Source Name'] = 'Please, select Referral Source from list';
  }

  if (!values.Insurances || values.Insurances.length === 0) {
    errors['Insurance Name'] = 'Please, select Payer from list';
  }

  if (validate.date(values['Authorization From'])) {
    errors['Authorization From'] = validate.date(values['Authorization From']);
  }
  if (!errors['Authorization From']) {
    delete errors['Authorization From'];
  }
  if (validate.date(values['Authorization To'])) {
    errors['Authorization To'] = validate.date(values['Authorization To']);
  }
  if (values['Authorization From']
    && !validate.date(values['Authorization From'])
    && values['Authorization To']
    && !validate.date(values['Authorization To'])
    && moment(values['Authorization To'], dateFormat)
      .isBefore(moment(values['Authorization From'], dateFormat))) {
    errors['Authorization To'] = 'Authorization To should be greater or equal Authorization From';
  }
  if (!errors['Authorization To']) {
    delete errors['Authorization To'];
  }
  if ((values['Authorization From'] || values['Authorization To']) && !values.Authorization) {
    errors.Authorization = 'Field is required in case of entered Authorization From / To';
  }

  // Untill this will be maerged https://github.com/indacloud/redux-form/pull/1
  errors.orderLine = values.orderLine ? values.orderLine.map((item) => {
    const orderLineErrors = {};
    if (!item.dateOfServiceFrom) {
      orderLineErrors.dateOfServiceFrom = 'Field is required';
    } else if (validate.date(item.dateOfServiceFrom)) {
      orderLineErrors.dateOfServiceFrom = validate.date(item.dateOfServiceFrom);
    }
    if (!orderLineErrors.dateOfServiceFrom) {
      delete orderLineErrors.dateOfServiceFrom;
    }
    if (!item.dateOfServiceTo) {
      orderLineErrors.dateOfServiceTo = 'Field is required';
    } else if (validate.date(item.dateOfServiceTo)) {
      orderLineErrors.dateOfServiceTo = validate.date(item.dateOfServiceTo);
    }
    if (item.dateOfServiceFrom
      && !validate.date(item.dateOfServiceFrom)
      && item.dateOfServiceTo
      && !validate.date(item.dateOfServiceTo)
      && moment(item.dateOfServiceTo, dateFormat)
        .isBefore(moment(item.dateOfServiceFrom, dateFormat))) {
      orderLineErrors.dateOfServiceTo = 'Date of Service To should be greater or equal Date of Service From';
    }
    if (!orderLineErrors.dateOfServiceTo) {
      delete orderLineErrors.dateOfServiceTo;
    }
    if (item.Type && orderLineTypes.indexOf(item.Type) === -1) {
      orderLineErrors.Type = 'Invalid Type';
    }
    if (item.dateOfDelivery && !item.timeOfDelivery) {
      orderLineErrors.timeOfDelivery = 'Time of Delivery is required if Date of Delivery is set';
    }
    if (item.timeOfDelivery && !item.dateOfDelivery) {
      orderLineErrors.dateOfDelivery = 'Date of Delivery is required if Time of Delivery is set';
    }
    if (item.Charge && item.Charge === '$0') {
      orderLineErrors.Charge = 'Charge should be more then 0';
    }
    if (item.Cost && item.Cost === '$0') {
      orderLineErrors.Cost = 'Cost should be more then 0';
    }

    if (!item.Status || item.Status.length === 0) {
      orderLineErrors['Status Name'] = 'Status is required';
    }

    return Object.keys(orderLineErrors).length > 0 ? orderLineErrors : undefined;
  }) : undefined;

  if (errors.orderLine === undefined
    || errors.orderLine.length === 0
    || errors.orderLine.filter(item => item).length === 0) {
    delete errors.orderLine;
  }
  return errors;
};

const OrderCreateEditForm = ({
  form,
  handleSubmit,
  formValues,
  error,
  Patient,
  order,
  onStockLineCreateButtonClick,
  classes,
  change,
  business,
  systemSettings,
  lazyInitialized,
  disabled,
}) => {
  const combinedDiagnosis = uniqBy([
    ...Patient && Patient.Diagnosis ? Patient.Diagnosis : [],
    ...order && order.Diagnosis ? order.Diagnosis : [],
  ], 'ID');
  const noStockLineErrorProduct = error && error.params && error.params['Product ID']
    && formValues.orderLine.length > 0
    ? formValues.orderLine.find(item =>
      item['Product ID'] === error.params['Product ID'])
    : null;
  return (
    <form
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      <Grid
        className={classes.gridContainer}
        container
        spacing={24}
        wrap="nowrap"
      >
        <Grid item xs={12} className={classes.gridItemShrink}>
          {error &&
            <FormError className={classes.formError}>
              {noStockLineErrorProduct ? (
                <Fragment>
                  {`Product "${noStockLineErrorProduct['Product Name']
                  }" has no inventory yet, please, specify at least price`}
                  {'\u00A0'}
                  <Link
                    onClick={onStockLineCreateButtonClick}
                    size="lg"
                    underline
                    bold
                  >
                    Add Inventory
                  </Link>
                </Fragment>
              ) : error}
            </FormError>
          }
          <ProductClaim
            form={form}
            Patient={Patient}
            {...order ? {
              Diagnosis: order.Diagnosis,
              'Order Insurance Param': order['Order Insurance Param'],
              Notes: order.Notes,
            } : {
              'Order Insurance Param': [],
            }}
            {...!order && !formValues['Diagnosis ID'] ? { Diagnosis: [] } : {}}
            {...formValues['Diagnosis ID']
              ? { Diagnosis: formValues['Diagnosis ID'].map(item =>
                combinedDiagnosis.filter(diagnosisItem => diagnosisItem.ID === item)[0])
                .filter(item => item) }
              : {}
            }
            systemSettings={systemSettings}
            business={business}
            formValues={formValues}
            lazyInitialized={lazyInitialized}
            {...order ? { Facility: order.Facility } : {}}
            {...!order && formValues['Facility ID'] && business && business.Facilities
              ? { Facility: business.Facilities.filter(item => item.ID === formValues['Facility ID'])[0] } : {}}
            disabled={disabled}
          />
        </Grid>
        <Grid
          className={classes.gridItemAside}
          item
        >
          <OrderInformation
            {...order}
            Patient={Patient}
            change={change}
            formValues={formValues}
            systemSettings={systemSettings}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </form>
  );
};

OrderCreateEditForm.propTypes = {
  form: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  formValues: PropTypes.shape({
    'Transmission Method': PropTypes.string,
  }),
  order: PropTypes.shape({
    ID: PropTypes.number,
  }),
  onStockLineCreateButtonClick: PropTypes.func.isRequired,
  Patient: PropTypes.shape({
    'Record Number': PropTypes.number,
    'Insurance Information': PropTypes.arrayOf(PropTypes.shape({
      ID: PropTypes.number,
    })),
  }),
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  systemSettings: PropTypes.object,
  lazyInitialized: PropTypes.bool,
  disabled: PropTypes.bool,
  classes: PropTypes.shape({
    formError: PropTypes.string.isRequired,
    gridItemShrink: PropTypes.string.isRequired,
    gridItemAside: PropTypes.string.isRequired,
  }).isRequired,
};

OrderCreateEditForm.defaultProps = {
  error: null,
  formValues: {},
  order: null,
  Patient: null,
  systemSettings: {},
  business: null,
  lazyInitialized: true,
  disabled: false,
};

export default reduxForm({
  onSubmit,
  onSubmitFail,
  onChange: throttle(onChange),
  validate: validateForm,
  enableReinitialize: true,
})(withStyles(styles)(OrderCreateEditForm));



// WEBPACK FOOTER //
// ./src/components/workflow/OrderCreateEditForm/OrderCreateEditForm.js